import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiStar, FiQuote } = FiIcons;

const Testimonials = () => {
  const testimonials = [
    {
      name: 'Sarah Chen',
      position: 'Founder & CEO',
      company: 'EcoTrack',
      image: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face',
      content: 'MVP72 delivered our sustainability platform in just 68 hours. The quality exceeded our expectations, and we were able to secure our first round of funding thanks to their work.',
      rating: 5,
      metrics: {
        timeline: '68 hours',
        result: 'Secured $500K funding'
      }
    },
    {
      name: 'Marcus Rodriguez',
      position: 'CTO',
      company: 'TaskFlow',
      image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face',
      content: 'The team at MVP72 understood our vision immediately and delivered a fully functional project management tool exactly on time. Their process is incredibly efficient.',
      rating: 5,
      metrics: {
        timeline: '72 hours',
        result: '2K+ active users'
      }
    },
    {
      name: 'Emily Watson',
      position: 'Product Manager',
      company: 'FoodieFinds',
      image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face',
      content: 'Working with MVP72 was a game-changer. They built our food discovery app with AI recommendations in 70 hours. The user engagement has been phenomenal.',
      rating: 5,
      metrics: {
        timeline: '70 hours',
        result: '8K+ downloads'
      }
    },
    {
      name: 'David Kim',
      position: 'Startup Founder',
      company: 'HealthSync',
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face',
      content: 'I needed to validate my health tech idea quickly. MVP72 built a working prototype that helped me test with real users and iterate based on feedback.',
      rating: 5,
      metrics: {
        timeline: '65 hours',
        result: 'Validated concept'
      }
    }
  ];

  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            What Our Clients Say
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Don't just take our word for it. Here's what founders and CTOs say about working with MVP72.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={testimonial.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl p-8 shadow-lg relative"
            >
              <div className="absolute top-6 right-6">
                <SafeIcon icon={FiQuote} className="w-8 h-8 text-blue-200" />
              </div>

              {/* Rating */}
              <div className="flex items-center mb-4">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <SafeIcon key={i} icon={FiStar} className="w-5 h-5 text-yellow-400 fill-current" />
                ))}
              </div>

              {/* Content */}
              <p className="text-gray-700 text-lg leading-relaxed mb-6">
                "{testimonial.content}"
              </p>

              {/* Metrics */}
              <div className="flex space-x-6 mb-6 p-4 bg-gray-50 rounded-lg">
                <div>
                  <div className="text-sm text-gray-500">Timeline</div>
                  <div className="font-semibold text-blue-600">{testimonial.metrics.timeline}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500">Result</div>
                  <div className="font-semibold text-green-600">{testimonial.metrics.result}</div>
                </div>
              </div>

              {/* Author */}
              <div className="flex items-center">
                <img
                  src={testimonial.image}
                  alt={testimonial.name}
                  className="w-12 h-12 rounded-full object-cover mr-4"
                />
                <div>
                  <div className="font-semibold text-gray-900">{testimonial.name}</div>
                  <div className="text-gray-600 text-sm">
                    {testimonial.position} at {testimonial.company}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Stats Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          viewport={{ once: true }}
          className="mt-16"
        >
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-white">
            <div className="grid md:grid-cols-4 gap-8 text-center">
              <div>
                <div className="text-3xl font-bold mb-2">99+</div>
                <div className="text-blue-100">MVPs Delivered</div>
              </div>
              <div>
                <div className="text-3xl font-bold mb-2">98%</div>
                <div className="text-blue-100">Client Satisfaction</div>
              </div>
              <div>
                <div className="text-3xl font-bold mb-2">67h</div>
                <div className="text-blue-100">Average Delivery</div>
              </div>
              <div>
                <div className="text-3xl font-bold mb-2">$2M+</div>
                <div className="text-blue-100">Funding Raised</div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Testimonials;